import { MapContainer as LeafletMap, TileLayer, ZoomControl, ScaleControl, Pane } from 'react-leaflet';
import { useMapEvents } from 'react-leaflet';
import { useMemo, useState, useEffect, useCallback } from 'react';
import L from 'leaflet';
import { LayerControl } from './Controls/LayerControl';
import { ToolsPanel } from './Controls/ToolsPanel';
import { ServiceControl } from './Controls/ServiceControl';
import { ResourceExplorerControl } from './Controls/ResourceExplorerControl';
import { BasemapControl } from './Controls/BasemapControl';
import { DrawControl } from './Controls/DrawControl';
import { MapSidebar } from './Controls/MapSidebar';
import { ResourceLayer } from './Layers/ResourceLayer';
import { GISLayer } from './Layers/GISLayer';
import { MiniMapControl } from './Controls/MiniMapControl';
import { MapArtLayer } from './Layers/MapArtLayer';
import { GIS_SERVICE_CATEGORIES, GIS_SERVICES, getServiceTypeLabel } from '../../data/gisServices';
import { RESOURCES, getResourceTypeLabel } from '../../data/resources';

const BASEMAPS = [
  {
    id: 'kids',
    label: 'طفولية',
    hint: 'ألوان مبهجة',
    preview: '🌈',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; OpenStreetMap &copy; CARTO'
  },
  {
    id: 'light',
    label: 'مبسطة',
    hint: 'واضحة للشرح',
    preview: '🧩',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap'
  },
  {
    id: 'dark',
    label: 'ليلية',
    hint: 'ألوان داكنة',
    preview: '🌙',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap'
  },
  {
    id: 'satellite',
    label: 'قمر صناعي',
    hint: 'صور حقيقية',
    preview: '🛰️',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri'
  }
];

const CoordinatesOverlay = ({ onMove }) => {
  useMapEvents({
    mousemove(event) {
      onMove([event.latlng.lat, event.latlng.lng]);
    },
    mouseout() {
      onMove(null);
    }
  });

  return null;
};

const buildBounds = (items) => {
  const points = items
    .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng))
    .map((item) => [item.lat, item.lng]);

  if (points.length === 0) return null;
  return L.latLngBounds(points);
};

const FANTASY_DECOR = [
  { id: 'cloud-1', icon: '☁️', className: 'map-fantasy-cloud map-fantasy-cloud-1' },
  { id: 'cloud-2', icon: '☁️', className: 'map-fantasy-cloud map-fantasy-cloud-2' },
  { id: 'star-1', icon: '✨', className: 'map-fantasy-star map-fantasy-star-1' },
  { id: 'star-2', icon: '🌟', className: 'map-fantasy-star map-fantasy-star-2' },
  { id: 'planet', icon: '🪐', className: 'map-fantasy-float map-fantasy-float-1' },
  { id: 'rocket', icon: '🚀', className: 'map-fantasy-float map-fantasy-float-2' },
  { id: 'rainbow', icon: '🌈', className: 'map-fantasy-float map-fantasy-float-3' },
  { id: 'kite', icon: '🪁', className: 'map-fantasy-float map-fantasy-float-4' }
];

export const MapContainer = () => {
  const [activeCategories, setActiveCategories] = useState(['minerals', 'energy', 'agriculture', 'tourism']);
  const [activeTool, setActiveTool] = useState(null);
  const [drawClearSignal, setDrawClearSignal] = useState(0);
  const [showDecorations, setShowDecorations] = useState(true);
  const [activeServiceCategories, setActiveServiceCategories] = useState(
    GIS_SERVICE_CATEGORIES.map((category) => category.id)
  );
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [autoOpenServiceId, setAutoOpenServiceId] = useState(null);
  const [autoOpenResourceId, setAutoOpenResourceId] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [resourceSearch, setResourceSearch] = useState('');
  const [showServices, setShowServices] = useState(true);
  const [showResources, setShowResources] = useState(true);
  const [coords, setCoords] = useState(null);
  const [activeBasemapId, setActiveBasemapId] = useState('kids');
  const [resourceTypeFilters, setResourceTypeFilters] = useState([]);
  const [resourceGovernorateFilters, setResourceGovernorateFilters] = useState([]);
  const [minImportance, setMinImportance] = useState(1);
  const [serviceTypeFilters, setServiceTypeFilters] = useState([]);
  const [kidMode, setKidMode] = useState(true);
  const [tipIndex, setTipIndex] = useState(0);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarTab, setSidebarTab] = useState('resources');
  const [autoFocusEnabled, setAutoFocusEnabled] = useState(true);

  const egyptCenter = [26.8206, 30.8025];
  const egyptBounds = [[22, 24.5], [32, 36.5]];

  const activeBasemap = BASEMAPS.find((basemap) => basemap.id === activeBasemapId) || BASEMAPS[0];

  const mapTips = useMemo(
    () => [
      'اضغط على أي رمز لمعرفة قصة المكان.',
      'استخدم الفلاتر لتظهر الموارد القريبة منك بسرعة.',
      'جرّب وضع الرسم وارسم طريق رحلتك على الخريطة.',
      'لون النجوم يوضح أهمية المكان للأطفال.'
    ],
    []
  );

  const resourceTypes = useMemo(() => Array.from(new Set(RESOURCES.map((item) => item.type))), []);
  const resourceGovernorates = useMemo(
    () => Array.from(new Set(RESOURCES.map((item) => item.governorate))),
    []
  );
  const serviceTypes = useMemo(() => Array.from(new Set(GIS_SERVICES.map((item) => item.type))), []);

  const visibleServices = useMemo(
    () =>
      GIS_SERVICES.filter((service) => activeServiceCategories.includes(service.category))
        .filter((service) =>
          serviceSearch
            ? `${service.name} ${service.description} ${service.type} ${getServiceTypeLabel(service.type)}`
                .toLowerCase()
                .includes(serviceSearch.toLowerCase())
            : true
        )
        .filter((service) =>
          serviceTypeFilters.length > 0 ? serviceTypeFilters.includes(service.type) : true
        ),
    [activeServiceCategories, serviceSearch, serviceTypeFilters]
  );

  const visibleResources = useMemo(
    () =>
      RESOURCES.filter((resource) => activeCategories.includes(resource.category))
        .filter((resource) =>
          resourceSearch
            ? `${resource.name} ${resource.description} ${resource.governorate} ${getResourceTypeLabel(resource.type)}`
                .toLowerCase()
                .includes(
                resourceSearch.toLowerCase()
              )
            : true
        )
        .filter((resource) =>
          resourceTypeFilters.length > 0 ? resourceTypeFilters.includes(resource.type) : true
        )
        .filter((resource) =>
          resourceGovernorateFilters.length > 0 ? resourceGovernorateFilters.includes(resource.governorate) : true
        )
        .filter((resource) => resource.importance >= minImportance),
    [activeCategories, resourceSearch, resourceTypeFilters, resourceGovernorateFilters, minImportance]
  );

  const resetView = useCallback(() => {
    if (!mapInstance) return;
    mapInstance.flyTo(egyptCenter, 6, { duration: 0.8 });
  }, [mapInstance]);

  const focusResource = useCallback(
    (resource) => {
      if (!resource || !mapInstance) return;
      setSelectedResourceId(resource.id);
      setSelectedServiceId(null);
      setAutoOpenResourceId(resource.id);
      setAutoOpenServiceId(null);
      mapInstance.flyTo([resource.lat, resource.lng], 9, { duration: 0.8 });
    },
    [mapInstance]
  );

  const focusService = useCallback(
    (service) => {
      if (!service || !mapInstance) return;
      setSelectedServiceId(service.id);
      setSelectedResourceId(null);
      setAutoOpenServiceId(service.id);
      setAutoOpenResourceId(null);
      mapInstance.flyTo([service.lat, service.lng], 9, { duration: 0.8 });
    },
    [mapInstance]
  );

  const fitResources = useCallback(() => {
    if (!mapInstance) return;
    const bounds = buildBounds(visibleResources);
    if (!bounds) return;
    setAutoOpenResourceId(null);
    mapInstance.fitBounds(bounds.pad(0.22), { animate: true, duration: 0.75, maxZoom: 10 });
  }, [mapInstance, visibleResources]);

  const fitServices = useCallback(() => {
    if (!mapInstance) return;
    const bounds = buildBounds(visibleServices);
    if (!bounds) return;
    setAutoOpenServiceId(null);
    mapInstance.fitBounds(bounds.pad(0.22), { animate: true, duration: 0.75, maxZoom: 10 });
  }, [mapInstance, visibleServices]);

  // Auto focus: debounced + only for the active tab.
  useEffect(() => {
    if (!autoFocusEnabled) return;
    if (!mapInstance) return;

    const isResourcesTab = sidebarTab === 'resources';
    const isServicesTab = sidebarTab === 'services';

    const id = setTimeout(() => {
      if (isResourcesTab && showResources) {
        if (visibleResources.length === 1) {
          focusResource(visibleResources[0]);
        } else if (visibleResources.length > 1) {
          fitResources();
        }
      }

      if (isServicesTab && showServices) {
        if (visibleServices.length === 1) {
          focusService(visibleServices[0]);
        } else if (visibleServices.length > 1) {
          fitServices();
        }
      }
    }, 450);

    return () => clearTimeout(id);
  }, [
    autoFocusEnabled,
    mapInstance,
    sidebarTab,
    showResources,
    showServices,
    visibleResources,
    visibleServices,
    focusResource,
    focusService,
    fitResources,
    fitServices
  ]);

  useEffect(() => {
    if (!kidMode) return undefined;
    const timer = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % mapTips.length);
    }, 4200);
    return () => clearInterval(timer);
  }, [kidMode, mapTips.length]);

  return (
    <div className={`relative w-full h-[calc(100vh-88px)] map-magic-shell ${kidMode ? 'map-kid-shell' : ''}`}>
      {kidMode && (
        <div className="map-kid-callout hidden md:flex">
          <span className="text-xl">🧒</span>
          <p>{mapTips[tipIndex]}</p>
          <button type="button" onClick={() => setTipIndex((prev) => (prev + 1) % mapTips.length)}>
            التالي ✨
          </button>
        </div>
      )}

      {kidMode && showDecorations && (
        <div className="map-fantasy-overlay" aria-hidden="true">
          {FANTASY_DECOR.map((item) => (
            <span key={item.id} className={item.className}>
              {item.icon}
            </span>
          ))}
        </div>
      )}

      <MapSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((value) => !value)}
        activeTab={sidebarTab}
        onChangeTab={setSidebarTab}
        renderResources={() => (
          <div className="space-y-3">
            <LayerControl
              activeCategories={activeCategories}
              onToggle={(category) => {
                setActiveCategories((prev) =>
                  prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
                );
              }}
            />
            <ResourceExplorerControl
              resources={visibleResources}
              activeCategories={activeCategories}
              selectedResourceId={selectedResourceId}
              searchQuery={resourceSearch}
              onSearchChange={setResourceSearch}
              onResourceSelect={focusResource}
              showResources={showResources}
              onToggleResources={setShowResources}
              availableTypes={resourceTypes}
              availableGovernorates={resourceGovernorates}
              activeTypes={resourceTypeFilters}
              activeGovernorates={resourceGovernorateFilters}
              minImportance={minImportance}
              onToggleType={(type) =>
                setResourceTypeFilters((prev) =>
                  prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
                )
              }
              onToggleGovernorate={(governorate) =>
                setResourceGovernorateFilters((prev) =>
                  prev.includes(governorate)
                    ? prev.filter((item) => item !== governorate)
                    : [...prev, governorate]
                )
              }
              onChangeImportance={setMinImportance}
            />
          </div>
        )}
        renderServices={() => (
          <ServiceControl
            categories={GIS_SERVICE_CATEGORIES}
            activeCategories={activeServiceCategories}
            onToggleCategory={(categoryId) => {
              setActiveServiceCategories((prev) =>
                prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
              );
            }}
            services={visibleServices}
            activeServiceId={selectedServiceId}
            onServiceSelect={focusService}
            searchQuery={serviceSearch}
            onSearchChange={setServiceSearch}
            showServices={showServices}
            onToggleShowServices={setShowServices}
            totalCount={GIS_SERVICES.length}
            availableTypes={serviceTypes}
            activeTypes={serviceTypeFilters}
            onToggleType={(type) =>
              setServiceTypeFilters((prev) =>
                prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
              )
            }
          />
        )}
        renderBasemap={() => (
          <BasemapControl basemaps={BASEMAPS} activeBasemapId={activeBasemapId} onChange={setActiveBasemapId} />
        )}
        renderTools={() => (
          <div className="space-y-3">
            <ToolsPanel
              activeTool={activeTool}
              onToolSelect={setActiveTool}
              onClearDrawings={() => setDrawClearSignal((value) => value + 1)}
              showDecorations={showDecorations}
              onToggleDecorations={setShowDecorations}
              kidMode={kidMode}
              onToggleKidMode={setKidMode}
            />

            <div className="glass-panel p-4 rounded-3xl map-panel-section">
              <div className="flex items-center justify-between">
                <p className="font-black text-gray-900">تحريك تلقائي</p>
                <input
                  type="checkbox"
                  checked={autoFocusEnabled}
                  onChange={() => setAutoFocusEnabled((value) => !value)}
                  className="w-4 h-4"
                />
              </div>
              <p className="text-xs text-gray-600 mt-2">
                عند تفعيلها: التصفية تحرك الخريطة لنتائجك (نتيجة واحدة = بوب-أب، أكثر من نتيجة = تكبير مناسب).
              </p>
            </div>
          </div>
        )}
        footer={(
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <span className="chip">🪨 موارد: <span className="number font-black">{visibleResources.length}</span></span>
              <span className="chip">🛰️ خدمات: <span className="number font-black">{visibleServices.length}</span></span>
              {coords && (
                <span className="chip number">📌 {coords[0].toFixed(3)}, {coords[1].toFixed(3)}</span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={resetView} className="map-action-btn map-action-btn-primary">
                رجوع للبداية
              </button>
              <button
                type="button"
                onClick={fitResources}
                className="map-action-btn"
                disabled={visibleResources.length < 2}
              >
                تكبير الموارد
              </button>
              <button
                type="button"
                onClick={fitServices}
                className="map-action-btn"
                disabled={visibleServices.length < 2}
              >
                تكبير الخدمات
              </button>
            </div>
          </div>
        )}
      />

      {/* Map */}
      <LeafletMap
        center={egyptCenter}
        zoom={6}
        minZoom={5}
        maxBounds={egyptBounds}
        maxBoundsViscosity={1.0}
        zoomControl={false}
        zoomSnap={0.5}
        zoomDelta={0.5}
        className={`w-full h-full ${kidMode ? 'map-candy-mode' : 'map-calm-mode'}`}
        whenCreated={setMapInstance}
      >
        <TileLayer url={activeBasemap.url} attribution={activeBasemap.attribution} maxZoom={19} />

        <ZoomControl position="topleft" />
        <ScaleControl position="bottomleft" />
        <CoordinatesOverlay onMove={setCoords} />
        <DrawControl activeTool={activeTool} clearSignal={drawClearSignal} />

        <Pane name="art" style={{ zIndex: 180 }}>
          <MapArtLayer
            show={showDecorations}
            resources={showResources ? visibleResources : []}
            services={showServices ? visibleServices : []}
            pane="art"
            kidMode={kidMode}
          />
        </Pane>

        {showResources && (
          <ResourceLayer
            categories={activeCategories}
            resources={visibleResources}
            selectedResourceId={selectedResourceId}
            onResourceSelect={focusResource}
            autoOpenResourceId={autoOpenResourceId}
            kidMode={kidMode}
          />
        )}

        {showServices && (
          <GISLayer
            services={visibleServices}
            onServiceSelect={focusService}
            selectedServiceId={selectedServiceId}
            autoOpenServiceId={autoOpenServiceId}
            kidMode={kidMode}
          />
        )}

        <MiniMapControl kidMode={kidMode} />
      </LeafletMap>
    </div>
  );
};
